import { useTranslation } from 'react-i18next';
import { useBilling } from '@/features/billing/hooks/useBilling';
import { FAMILY_ANNUAL } from '@/features/billing/lib/getRedirectUrl';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Button, Flex, Text } from '@/ui';

type Props = {
  /**
   * Manual grants can't be upgraded in place (no Stripe subscription behind
   * them), so the copy has to frame Family as a separate purchase.
   */
  isManual?: boolean;
};

// Shown to anyone who has Plus but isn't in a family yet. A live Plus
// subscriber is routed by the server into the in-place portal upgrade; a manual
// grant or an org-granted user reaches a fresh Family checkout — one client
// entry point either way.
export const FamilyPromoPanel = ({ isManual = false }: Props) => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'subscription.family_promo',
  });
  const { redirectToCheckout } = useBilling();
  const { trackEvent } = useAnalytics();

  const handleUpgrade = async () => {
    trackEvent('settings:click upgrade', {
      target: 'button',
      launch: 'settings:subscription',
    });
    await redirectToCheckout(
      'yearly',
      { launch: 'settings:subscription' },
      FAMILY_ANNUAL
    );
  };

  return (
    <Flex
      align={{ base: 'stretch', md: 'center' }}
      bg="gray.100"
      borderRadius="ah-xl"
      direction={{ base: 'column', md: 'row' }}
      gap="4"
      justify="space-between"
      mt="10"
      p="6"
    >
      <Flex direction="column" gap="1" maxW="md">
        <Text fontSize="lg" fontWeight="semibold">
          {t('heading')}
        </Text>
        <Text color="customgray.mid" fontSize="sm">
          {t(isManual ? 'body_manual' : 'body')}
        </Text>
      </Flex>
      <Button flexShrink="0" onClick={handleUpgrade}>
        {t('button')}
      </Button>
    </Flex>
  );
};
